import { TMasterOccupation } from "@/api/entities/master-occupation.entity";
import { useCreateMasterOccupation, useUpdateMasterOccupation } from "@/api/services/global/master";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { OCCUPATION_TYPE, OCCUPATION_TYPE_LABEL } from "@/features/master-data/constants/occupationType";
import { useConfirmCloseModal } from "@/features/master-data/hooks/useConfirmCloseModal";
import { masterDataSagyoSchema } from "@/features/master-data/schema/masterDataOccupationSchema";
import { TMasterDataSagyoFormValues } from "@/features/master-data/types/masterDataOccupationForm";
import { setErrorForm, showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack, Typography } from "@mui/material";
import { FC, useMemo } from "react";
import { useForm } from "react-hook-form";

type TSagyoFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** 編集対象。未指定なら新規追加 */
  selectedOccupation?: TMasterOccupation;
  /** 新規追加時の親（職種）ID */
  parentId?: number;
  /** 親の名称。表示のみ */
  parentName?: string;
};

export const SagyoFormModal: FC<TSagyoFormModalProps> = ({ isOpen, onClose, selectedOccupation, parentId, parentName }) => {
  const createMutation = useCreateMasterOccupation();
  const updateMutation = useUpdateMasterOccupation();

  const defaultValues = useMemo(
    () => ({
      name: selectedOccupation?.name ?? undefined,
      examName: selectedOccupation?.examName ?? undefined,
      level1Name: selectedOccupation?.level1Name ?? undefined,
      level2Name: selectedOccupation?.level2Name ?? undefined,
      level3Name: selectedOccupation?.level3Name ?? undefined,
    }),
    [selectedOccupation],
  );

  const methods = useForm<TMasterDataSagyoFormValues>({
    resolver: yupResolver(masterDataSagyoSchema),
    defaultValues,
    mode: "all",
    reValidateMode: "onBlur",
  });

  const {
    control,
    formState: { isDirty },
  } = methods;
  const { confirmClose, handleRequestClose } = useConfirmCloseModal(isDirty, onClose);

  const onSubmit = async (data: TMasterDataSagyoFormValues) => {
    try {
      // 試験区分（KENTEI／HYOKA）は試験名からサーバー側で導出するため送らない
      const payload = {
        name: data.name,
        examName: data.examName,
        level1Name: data.level1Name,
        level2Name: data.level2Name,
        level3Name: data.level3Name,
      };

      const rs = selectedOccupation
        ? await updateMutation.mutateAsync({ id: toNumber(selectedOccupation.id), payload })
        : await createMutation.mutateAsync({ ...payload, type: OCCUPATION_TYPE.SAGYO, parentId });

      toast.success(rs.message ?? API_SUCCESS_MESSAGE);

      onClose();
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Modal
      width={760}
      title={`${OCCUPATION_TYPE_LABEL[OCCUPATION_TYPE.SAGYO]}の${selectedOccupation ? "編集" : "追加"}`}
      isOpen={isOpen}
      enableFooter={false}
      onClose={handleRequestClose}
      enableCloseIcon={false}
    >
      <Form methods={methods} onSubmit={onSubmit} schema={masterDataSagyoSchema}>
        <Grid container spacing={3} py={3}>
          {parentName && (
            <>
              <Grid item xs={6}>
                <FormField label="職種">
                  <Typography variant="body14">{parentName}</Typography>
                </FormField>
              </Grid>
              <Grid item xs={6} />
            </>
          )}
          <Grid item xs={12}>
            <FormField control={control} name="name" label={OCCUPATION_TYPE_LABEL[OCCUPATION_TYPE.SAGYO]}>
              <Input />
            </FormField>
          </Grid>
          <>
            <Grid item xs={12}>
              <FormField control={control} name="examName" label="試験名">
                <Input />
              </FormField>
            </Grid>
            <Grid item xs={4}>
              <FormField control={control} name="level1Name" label="1号修了時の級">
                <Input />
              </FormField>
            </Grid>
            <Grid item xs={4}>
              <FormField control={control} name="level2Name" label="2号修了時の級">
                <Input />
              </FormField>
            </Grid>
            <Grid item xs={4}>
              <FormField control={control} name="level3Name" label="3号修了時の級">
                <Input />
              </FormField>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="cap12">3号が無い作業の場合は「3号修了時の級」を空欄にしてください。</Typography>
            </Grid>
          </>
        </Grid>
        <Stack flexDirection="row" justifyContent="center" gap="24px">
          <Button variant="outline" onClick={handleRequestClose}>
            キャンセル
          </Button>
          <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending}>
            保存
          </Button>
        </Stack>
      </Form>
      <ConfirmSaveModal open={confirmClose.open} onOk={confirmClose.confirm} onClose={confirmClose.cancel}>
        <Typography>入力したデータがなくなりますが、よろしいですか。</Typography>
      </ConfirmSaveModal>
    </Modal>
  );
};
